// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// API Service class
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage or sessionStorage
  getAuthToken() {
    // Check localStorage first (Remember Me enabled), then sessionStorage
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  // Set auth token to localStorage
  setAuthToken(token) {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  // Get default headers
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Check if this is a FormData request
    const isFormData = options.body instanceof FormData;
    
    // Get auth token
    const token = this.getAuthToken();
    
    const config = {
      headers: isFormData 
        ? { 
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type for FormData - let browser set it with boundary
            ...options.headers // Allow custom headers to be passed
          }
        : this.getHeaders(),
      ...options,
    };

    // Extract signal from options if provided (for request cancellation)
    const externalSignal = options.signal;
    delete config.signal; // Remove from config to avoid passing to fetch twice

    try {
      // Create AbortController for timeout 
      // Default: 5 minutes for file uploads (FormData), 60 seconds for regular requests
      // This handles slow connections and large file uploads better
      const timeoutController = new AbortController();
      const timeout = options.timeout || (isFormData ? 300000 : 60000); // 5 minutes for uploads, 60s for regular
      const timeoutId = setTimeout(() => timeoutController.abort(), timeout);
      
      // Combine external signal (if provided) with timeout signal
      let combinedSignal = timeoutController.signal;
      if (externalSignal) {
        // Check if external signal is already aborted
        if (externalSignal.aborted) {
          clearTimeout(timeoutId);
          throw new DOMException('The operation was aborted.', 'AbortError');
        }
        
        // Create a combined signal that aborts if either signal aborts
        const combinedController = new AbortController();
        
        // Listen to external signal abort
        externalSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          combinedController.abort();
        });
        
        // Listen to timeout signal abort
        timeoutController.signal.addEventListener('abort', () => {
          combinedController.abort();
        });
        
        combinedSignal = combinedController.signal;
      }
      
      const fetchConfig = {
        ...config,
        signal: combinedSignal
      };
      
      const response = await fetch(url, fetchConfig);
      clearTimeout(timeoutId);
      
      // Handle other errors first
      if (!response.ok) {
        let errorData = {};
        try {
          const text = await response.text();
          if (text) {
            errorData = JSON.parse(text);
          }
        } catch (parseError) {
          // If JSON parsing fails, use empty object
          errorData = {};
        }
        
        // Create a custom error that preserves the original response data
        const error = new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
        error.response = { data: errorData || {}, status: response.status };
        throw error;
      }

      // Return JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout: The server is taking too long to respond. Please try again.');
      }
      // Handle network errors
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        throw new Error('Network error: Unable to connect to server. Please check your connection and try again.');
      }
      throw error;
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  // POST request
  async post(endpoint, data) {
    const isFormData = data instanceof FormData;
    
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // PATCH request
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

// Create and export API service instance
const apiService = new ApiService();
export default apiService; 